import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, LanguageContext } from "../../../../Context";
import useDBO from "../../hooks/useDBO";
import mut from "../../hooks/mut";
import Lang from "../../components/Lang";
import { usePrimaryColor } from "../../components/PrimaryColorContext";
import {
  CalendarClock,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  Sparkles,
  User,
  ArrowRight,
  ChevronUp,
} from "lucide-react";


/**
 * Leave Approvals page - allows managers and admins to review,
 * approve, or decline pending leave requests from their staff.
 * Fetches data from /leave-requests/pending with 30s polling.
 *
 * @author Forge
 * @date 2026-04-08
 * @version 1.0
 */
export default function LeaveApprovals() {
  const { user, checkRoles } = useContext(UserContext);
  const { primaryColor } = usePrimaryColor();
  const { l, language } = useContext(LanguageContext);
  const navigate = useNavigate();

  const leaveRequests = useDBO("/leave-requests/pending", 30);
  const [actioningId, setActioningId] = useState(null);
  const [declineReasons, setDeclineReasons] = useState({});
  const [expandedDecline, setExpandedDecline] = useState(null);

  const isManager =
    checkRoles?.(["Manager", "Admin", "Owner", "Human resources"]) || false;

  if (!isManager) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-[#f8fafc] text-[#1e293b]">
        <div className="home-widget p-12 text-center">
          <p className="text-slate-400 text-lg">
            You do not have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString(
      language === 0 ? "en-US" : "vi-VN",
      { year: "numeric", month: "short", day: "numeric" }
    );

  const handleApprove = async (id) => {
    setActioningId(id);
    try {
      const res = await mut("put", `/leave-requests/${id}/approve`);
      if (res) leaveRequests.remove(id);
    } finally {
      setActioningId(null);
    }
  };

  const handleDecline = async (id) => {
    const reason = declineReasons[id];
    if (!reason || !reason.trim()) return;
    setActioningId(id);
    try {
      const res = await mut("put", `/leave-requests/${id}/decline`, {
        declinedReason: reason.trim(),
      });
      if (res) leaveRequests.remove(id);
    } finally {
      setActioningId(null);
      setExpandedDecline(null);
    }
  };

  const pending = leaveRequests.get || [];

  return (
    <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-[#f8fafc] text-[#1e293b]">
      <div className="flex justify-between items-center mb-6 pb-3 border-b border-[#e2e8f0]">
        <h1 className={`text-[2rem] font-extrabold text-${primaryColor}-800 tracking-tight m-0 flex items-center gap-3`}>
          <CalendarClock size={28} />
          <Lang w="leaveApprovals" />
        </h1>
        {pending.length > 0 && (
          <span className="bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-sm font-bold">
            {pending.length} <Lang w="pending" />
          </span>
        )}
      </div>

      {leaveRequests.loading && !leaveRequests.get && (
        <div className="home-widget p-12 text-center flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-slate-300" />
          <p className="text-slate-400">
            <Lang w="loadingLeave" />
          </p>
        </div>
      )}

      {leaveRequests.get && pending.length === 0 && (
        <div className="home-widget p-12 text-center flex flex-col items-center gap-3">
          <Sparkles size={40} className="text-slate-300 mb-2" />
          <p className="text-slate-400 text-lg font-medium">
            <Lang w="noPendingLeave" />
          </p>
        </div>
      )}

      {leaveRequests.get && pending.length > 0 && (
        <div className="flex flex-col gap-4">
          {pending.map((request) => {
            const isActioning = actioningId === request._id;
            const isDeclineExpanded = expandedDecline === request._id;
            const staffName =
              request.user?.name || request.user || "Unknown";

            return (
              <div
                key={request._id}
                className="bg-white border border-slate-200 rounded-[16px] shadow-sm p-6 transition-all duration-200 hover:shadow-md"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <User size={18} className="text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900">
                        {staffName}
                      </h3>
                      <p className="text-xs text-slate-400">
                        <Lang w="applied" />: {formatDate(request.createdAt)}
                      </p>
                    </div>
                  </div>
                  <span className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                    <span className="bg-amber-400 w-2 h-2 rounded-full" />
                    <Lang w="pending" />
                  </span>
                </div>

                <div className="flex items-center gap-4 mb-3 bg-slate-50 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-slate-400" />
                    <span className="text-sm font-semibold text-slate-700">
                      {formatDate(request.startDate)}
                    </span>
                  </div>
                  <ArrowRight size={14} className="text-slate-300" />
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-slate-400" />
                    <span className="text-sm font-semibold text-slate-700">
                      {formatDate(request.endDate)}
                    </span>
                  </div>
                </div>

                <p className="text-slate-600 text-sm mb-4 pl-1">
                  {request.reason}
                </p>

                {isDeclineExpanded && (
                  <div className="mb-4 flex gap-2">
                    <input
                      type="text"
                      className="profile-input flex-1"
                      placeholder={l["enterDeclineReason"][language]}
                      value={declineReasons[request._id] || ""}
                      onChange={(e) =>
                        setDeclineReasons({
                          ...declineReasons,
                          [request._id]: e.target.value,
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleDecline(request._id);
                      }}
                      autoFocus
                    />
                    <button
                      onClick={() => handleDecline(request._id)}
                      disabled={
                        !declineReasons[request._id]?.trim() || isActioning
                      }
                      className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-xl font-bold text-sm hover:bg-red-700 transition-all disabled:opacity-50"
                    >
                      {isActioning ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <XCircle size={14} />
                      )}
                      <Lang w="submit" />
                    </button>
                    <button
                      onClick={() => setExpandedDecline(null)}
                      className="px-3 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-200 transition-all"
                    >
                      <ChevronUp size={14} />
                    </button>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleApprove(request._id)}
                    disabled={isActioning}
                    className="flex items-center gap-1.5 px-5 py-2 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-700 transition-all disabled:opacity-50"
                  >
                    {isActioning ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <CheckCircle size={14} />
                    )}
                    <Lang w="approveLeave" />
                  </button>

                  {!isDeclineExpanded && (
                    <button
                      onClick={() => setExpandedDecline(request._id)}
                      disabled={isActioning}
                      className="flex items-center gap-1.5 px-5 py-2 bg-red-100 text-red-700 rounded-xl font-bold text-sm hover:bg-red-200 transition-all disabled:opacity-50"
                    >
                      <XCircle size={14} />
                      <Lang w="declineLeave" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
